import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import shipmentClientService from "../../services/ShipmentClientService.js"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { MapPin, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react"
import { PageLayout, Button } from "../common/index.js"

const LABEL_CLS = "block text-xs font-medium text-secondary mb-1"
const INPUT_CLS =
  "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-gray-100 disabled:cursor-not-allowed"
const INPUT_ERROR_CLS = "border-red-300 focus:border-red-500 focus:ring-red-200"

const hasExtraPartyData = (address, email, gst) =>
  !!(String(address || "").trim() || String(email || "").trim() || String(gst || "").trim())

const isHubLocation = (locationName, hubs) =>
  hubs.some((h) => (h.location || "").trim().toLowerCase() === (locationName || "").trim().toLowerCase())

const EditShipment = () => {
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [showGSTDetails, setShowGSTDetails] = useState(false)
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false)
  const [showConsignorExtra, setShowConsignorExtra] = useState(false)
  const [showConsigneeExtra, setShowConsigneeExtra] = useState(false)
  const [showShipmentExtra, setShowShipmentExtra] = useState(false)
  const [consignerHighlightIndex, setConsignerHighlightIndex] = useState(-1)
  const [consigneeHighlightIndex, setConsigneeHighlightIndex] = useState(-1)
  const consignerLocationRef = useRef(null)
  const consigneeLocationRef = useRef(null)

  const [showArticleDropdown, setShowArticleDropdown] = useState(false)
  const [articleSearchMode, setArticleSearchMode] = useState(false)
  const [articleHighlightIndex, setArticleHighlightIndex] = useState(-1)
  const articleTypes = ["BUNDLE","THERMOCOL BOX","ROLL","GUNNU BAGS","CARTON","CAN","AUTO PARTS","METAL","SHEET","BRIEFCASE","ENVELOP","BOX","PACKAGE","PARCEL","CRATE","DRUM","BAG","PALLET","OTHER"]

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [consignerLocations, setConsignerLocations] = useState([])
  const [consigneeLocations, setConsigneeLocations] = useState([])
  const [consignerLocationSearch, setConsignerLocationSearch] = useState("")
  const [consigneeLocationSearch, setConsigneeLocationSearch] = useState("")
  const [showConsignerDropdown, setShowConsignerDropdown] = useState(false)
  const [showConsigneeDropdown, setShowConsigneeDropdown] = useState(false)

  const [consignerSuggestions, setConsignerSuggestions] = useState([])
  const [showConsignerSuggestions, setShowConsignerSuggestions] = useState(false)
  const [consigneeSuggestions, setConsigneeSuggestions] = useState([])
  const [showConsigneeSuggestions, setShowConsigneeSuggestions] = useState(false)

  const [useShipmentClient, setUseShipmentClient] = useState(false)
  const [shipmentClients, setShipmentClients] = useState([])
  const [clientSearch, setClientSearch] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)

  const [showEwayBillModal, setShowEwayBillModal] = useState(false)
  const [showViewEwayBillModal, setShowViewEwayBillModal] = useState(false)
  const [ewayBills, setEwayBills] = useState([""])
  const [submittedEwayBills, setSubmittedEwayBills] = useState([])
  const [existingEwayBills, setExistingEwayBills] = useState([])
  const [loadingEwayBills, setLoadingEwayBills] = useState(false)

  const [originalCharges, setOriginalCharges] = useState({
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    late_charges: "",
    docket_fee: "",
    other_charges: "",
    discount: "",
  })

  const [formData, setFormData] = useState({
    consigner_name: "",
    consigner_address: "",
    consigner_location: "",
    consigner_contact: "",
    consigner_email: "",
    consigner_gst: "",
    consignee_name: "",
    consignee_address: "",
    consignee_location: "",
    consignee_contact: "",
    consignee_email: "",
    consignee_gst: "",
    invoice_number: "",
    no_of_articles: 1,
    article_type: "",
    article_amount: "",
    weight: "",
    shipment_value: "",
    freight_charges: "",
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    late_charges: "",
    docket_fee: "",
    other_charges: "",
    discount: "",
    delivery_type: "hub_delivery",
    mode_of_payment: "to_pay",
    payment_mode: "",
    pickup_type: "partial_pickup",
    parcel_detail: "",
    parcel_image_1: null,
    parcel_image_2: null,
    paid_amount: "",
    sgst_percentage: "",
    cgst_percentage: "",
    igst_percentage: "",
    status: "Booked",
  })

  const [imagePreview, setImagePreview] = useState({ img1: null, img2: null })
  const [existingImages, setExistingImages] = useState({ img1: null, img2: null })

  const isAgentRole = user?.role === "Agent"
  const isValidEway = (value) => /^\d{12}$/.test(value)

  useEffect(() => {
    fetchShipment()
    fetchLocationHubs()
    fetchShipmentClients()
  }, [shipmentId])

  useEffect(() => {
    if (useShipmentClient || !hasExtraPartyData(formData.consigner_address, formData.consigner_email, formData.consigner_gst)) {
      setShowConsignorExtra(false)
    }
  }, [formData.consigner_address, formData.consigner_email, formData.consigner_gst, useShipmentClient])

  useEffect(() => {
    if (!hasExtraPartyData(formData.consignee_address, formData.consignee_email, formData.consignee_gst)) {
      setShowConsigneeExtra(false)
    }
  }, [formData.consignee_address, formData.consignee_email, formData.consignee_gst])

  const filteredConsignerLocations = consignerLocations.filter((loc) =>
    loc.location.toLowerCase().includes(consignerLocationSearch.toLowerCase()),
  )

  const filteredConsigneeLocations = consigneeLocations.filter((loc) =>
    loc.location.toLowerCase().includes(consigneeLocationSearch.toLowerCase()),
  )

  const filteredArticleTypes = articleTypes.filter(
    (t) => !articleSearchMode || !formData.article_type || t.includes(formData.article_type),
  )

  const filteredClients = shipmentClients.filter(
    (client) =>
      client.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.company_name.toLowerCase().includes(clientSearch.toLowerCase()),
  )

  const hasConsignorExtraData =
    !useShipmentClient && hasExtraPartyData(formData.consigner_address, formData.consigner_email, formData.consigner_gst)
  const hasConsigneeExtraData = hasExtraPartyData(
    formData.consignee_address,
    formData.consignee_email,
    formData.consignee_gst,
  )

  const totalEwayBillCount = existingEwayBills.length + submittedEwayBills.length

  const handleCloseEwayBillModal = () => {
    setShowEwayBillModal(false)
    setEwayBills([""])
  }

  const handleSubmitEwayBills = async () => {
    const validBills = ewayBills.filter(isValidEway)
    if (validBills.length === 0) {
      Alert.error("Please enter at least one valid E-way bill")
      return
    }

    const allBills = [...existingEwayBills.map((b) => b.eway_bill_number), ...submittedEwayBills, ...validBills]
    const uniqueBills = [...new Set(allBills)]

    if (uniqueBills.length !== allBills.length) {
      Alert.error("Duplicate E-way bill numbers found")
      return
    }

    setSubmittedEwayBills((prev) => [...prev, ...validBills])
    Alert.success(`${validBills.length} E-way bill(s) added successfully`)
    handleCloseEwayBillModal()
  }

  const handleViewEwayBills = () => {
    if (totalEwayBillCount === 0) {
      Alert.info("No E-way bills added yet")
      return
    }
    setShowViewEwayBillModal(true)
  }

  const handleRemoveSubmittedEwayBill = (indexToRemove) => {
    const updated = submittedEwayBills.filter((_, index) => index !== indexToRemove)
    setSubmittedEwayBills(updated)
    if (updated.length === 0 && existingEwayBills.length === 0) {
      setShowViewEwayBillModal(false)
    }
    Alert.success("E-way bill removed from queue")
  }

  const handleDeleteEwayBill = async (ewayBillId, ewayBillNumber) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete E-way bill ${ewayBillNumber}? This action cannot be undone.`,
      "Confirm Delete",
    )

    if (!confirmed) return

    try {
      setLoadingEwayBills(true)
      await shipmentService.deleteEwayBill(ewayBillId)
      Alert.success("E-way bill deleted successfully")
      await fetchEwayBills()
      if (existingEwayBills.length <= 1 && submittedEwayBills.length === 0) {
        setShowViewEwayBillModal(false)
      }
    } catch (error) {
      console.error("Error deleting e-way bill:", error)
      Alert.error(error.response?.data?.error || "Failed to delete E-way bill")
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const fetchEwayBills = async () => {
    try {
      setLoadingEwayBills(true)
      const response = await shipmentService.getEwayBills(shipmentId)
      setExistingEwayBills(response.data.eway_bills || [])
    } catch (error) {
      console.error("Error fetching e-way bills:", error)
      setExistingEwayBills([])
    } finally {
      setLoadingEwayBills(false)
    }
  }

  const fetchShipmentClients = async () => {
    try {
      const response = await shipmentClientService.getAll(1, 1000, { status: "active" })
      setShipmentClients(response.data || [])
    } catch (error) {
      console.error("Error fetching shipment clients:", error)
    }
  }

  const fetchCustomerSuggestions = async (type, name = "", contact = "") => {
    if (isAgentRole) return
    if (!name && !contact) {
      type === "consigner" ? setConsignerSuggestions([]) : setConsigneeSuggestions([])
      return
    }
    try {
      const res = await shipmentService.getCustomerSuggestions(name, contact)
      const payload = res.data?.data || res.data || {}
      const suggestions =
        type === "consigner"
          ? Array.isArray(payload.senders)
            ? payload.senders
            : []
          : Array.isArray(payload.receivers)
            ? payload.receivers
            : []
      type === "consigner" ? setConsignerSuggestions(suggestions) : setConsigneeSuggestions(suggestions)
    } catch (err) {
      console.error("Error fetching customer suggestions:", err)
    }
  }

  const handleSelectSuggestion = (type, suggestion) => {
    const prefix = type === "consigner" ? "consigner" : "consignee"
    setFormData((prev) => ({
      ...prev,
      [`${prefix}_name`]: suggestion.name || "",
      [`${prefix}_contact`]: suggestion.contact || "",
      [`${prefix}_address`]: suggestion.address || "",
      [`${prefix}_email`]: suggestion.email || "",
      [`${prefix}_gst`]: suggestion.gst || "",
    }))
    if (type === "consigner") {
      setShowConsignorExtra(false)
      setConsignerSuggestions([])
      setShowConsignerSuggestions(false)
    } else {
      setShowConsigneeExtra(false)
      setConsigneeSuggestions([])
      setShowConsigneeSuggestions(false)
    }
  }

  const handleClientSelect = (client) => {
    setSelectedClient(client)
    setShowConsignorExtra(false)
    setFormData({
      ...formData,
      consigner_name: client.full_name,
      consigner_address: client.address,
      consigner_contact: client.contact,
      consigner_email: client.email,
      consigner_gst: client.gst_number || "",
      shipment_client_id: client.id,
    })
    setShowClientDropdown(false)
    setClientSearch("")
  }

  const handleUseClientChange = (checked) => {
    setUseShipmentClient(checked)
    if (checked) {
      setShowConsignorExtra(false)
    }
    if (!checked) {
      setSelectedClient(null)
      setClientSearch("")
      setShowClientDropdown(false)
    }
  }

  const fetchShipment = async () => {
    try {
      setLoading(true)
      const response = await shipmentService.getShipment(shipmentId)
      const shipment = response.data.shipment || response.data.data

      if (shipment.is_deleted) {
        Alert.error("This shipment is deleted and cannot be edited")
        navigate("/shipments")
        return
      }

      const mappedShipment = {
        ...shipment,
        consigner_name: shipment.consigner_name || shipment.sender_name,
        consigner_address: shipment.consigner_address || shipment.sender_address,
        consigner_location: shipment.consigner_location || shipment.sender_location,
        consigner_contact: shipment.consigner_contact || shipment.sender_contact,
        consigner_email: shipment.consigner_email || shipment.sender_email,
        consigner_gst: shipment.consigner_gst || shipment.sender_gst,
        consignee_name: shipment.consignee_name || shipment.receiver_name,
        consignee_address: shipment.consignee_address || shipment.receiver_address,
        consignee_location: shipment.consignee_location || shipment.receiver_location,
        consignee_contact: shipment.consignee_contact || shipment.receiver_contact,
        consignee_email: shipment.consignee_email || shipment.receiver_email,
        consignee_gst: shipment.consignee_gst || shipment.receiver_gst,
        invoice_number: shipment.invoice_number,
      }

      setFormData((prev) => ({
        ...prev,
        ...mappedShipment,
        weight: mappedShipment.weight ?? "",
        shipment_value: mappedShipment.shipment_value ?? "",
        article_amount: mappedShipment.article_amount ?? "",
        freight_charges: mappedShipment.freight_charges ?? "",
        handling_charges: mappedShipment.handling_charges ?? "",
        delivery_charges: mappedShipment.delivery_charges ?? "",
        door_pickup_charges: mappedShipment.door_pickup_charges ?? "",
        late_charges: mappedShipment.late_charges ?? "",
        docket_fee: mappedShipment.docket_fee ?? "",
        other_charges: mappedShipment.other_charges ?? "",
        discount: mappedShipment.discount ?? "",
        sgst_percentage: mappedShipment.sgst_percentage ?? "",
        cgst_percentage: mappedShipment.cgst_percentage ?? "",
        igst_percentage: mappedShipment.igst_percentage ?? "",
        paid_amount: mappedShipment.paid_amount ?? "",
        parcel_image_1: null,
        parcel_image_2: null,
      }))

      setOriginalCharges({
        handling_charges: shipment.handling_charges || "",
        delivery_charges: shipment.delivery_charges || "",
        door_pickup_charges: shipment.door_pickup_charges || "",
        late_charges: shipment.late_charges || "",
        docket_fee: shipment.docket_fee ?? "",
        other_charges: shipment.other_charges || "",
        discount: shipment.discount || "",
      })

      if (shipment.parcel_image_1) {
        setExistingImages((prev) => ({
          ...prev,
          img1: `data:image/jpeg;base64,${shipment.parcel_image_1}`,
        }))
      }

      if (shipment.parcel_image_2) {
        setExistingImages((prev) => ({
          ...prev,
          img2: `data:image/jpeg;base64,${shipment.parcel_image_2}`,
        }))
      }
    } catch (error) {
      console.error("Error fetching shipment:", error)
      Alert.error("Failed to load shipment")
      navigate("/shipments")
    } finally {
      setLoading(false)
    }

    fetchEwayBills()
  }

  const fetchLocationHubs = async () => {
    try {
      const response = await shipmentService.getAllLocationHubs()
      const allLocations = response.data.hubs || []
      setConsignerLocations(allLocations)
      setConsigneeLocations(allLocations)
    } catch (error) {
      console.error("Error fetching location hubs:", error)
    }
  }

  const calculateFreightCharges = () => {
    const articleAmount = Number.parseFloat(formData.article_amount) || 0
    const noOfArticles = Number.parseFloat(formData.no_of_articles) || 1
    return (articleAmount * noOfArticles).toFixed(2)
  }

  const calculateTotalAmount = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = Number.parseFloat(formData.handling_charges) || 0
    const deliveryCharges = Number.parseFloat(formData.delivery_charges) || 0
    const doorPickupCharges = Number.parseFloat(formData.door_pickup_charges) || 0
    const docketFee = Number.parseFloat(formData.docket_fee) || 0
    const lateCharges = Number.parseFloat(formData.late_charges) || 0
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const discount = Number.parseFloat(formData.discount) || 0

    const subtotal = Math.max(
      0,
      freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + lateCharges + otherCharges - discount,
    )

    const sgst = (freightCharges * (Number.parseFloat(formData.sgst_percentage) || 0)) / 100
    const cgst = (freightCharges * (Number.parseFloat(formData.cgst_percentage) || 0)) / 100
    const igst = (freightCharges * (Number.parseFloat(formData.igst_percentage) || 0)) / 100

    return (subtotal + sgst + cgst + igst).toFixed(2)
  }

  const calculateGSTAmounts = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const sgst = (freightCharges * (Number.parseFloat(formData.sgst_percentage) || 0)) / 100
    const cgst = (freightCharges * (Number.parseFloat(formData.cgst_percentage) || 0)) / 100
    const igst = (freightCharges * (Number.parseFloat(formData.igst_percentage) || 0)) / 100
    return { sgst: sgst.toFixed(2), cgst: cgst.toFixed(2), igst: igst.toFixed(2) }
  }

  const calculateBalanceAmount = () => {
    const totalAmount = Number.parseFloat(calculateTotalAmount())
    const paid = Number.parseFloat(formData.paid_amount) || 0
    return (totalAmount - paid).toFixed(2)
  }

  const validateAgentChargeReduction = (fieldName, newValue) => {
    if (!isAgentRole) return true

    const originalValue = Number.parseFloat(originalCharges[fieldName]) || 0
    const newVal = Number.parseFloat(newValue) || 0

    if (newVal < originalValue) {
      Alert.error(
        `As an Agent, you cannot reduce ${fieldName.replace(/_/g, " ")}. You can only keep it the same or increase it.`,
      )
      return false
    }
    return true
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target

    const chargeFields = [
      "freight_charges",
      "handling_charges",
      "delivery_charges",
      "door_pickup_charges",
      "late_charges",
      "docket_fee",
      "other_charges",
      "discount",
      "paid_amount",
      "article_amount",
      "sgst_percentage",
      "cgst_percentage",
      "igst_percentage",
    ]

    if (chargeFields.includes(name)) {
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
        const restrictedCharges = [
          "handling_charges",
          "delivery_charges",
          "door_pickup_charges",
          "late_charges",
          "docket_fee",
          "other_charges",
        ]

        if (restrictedCharges.includes(name) && !validateAgentChargeReduction(name, value)) {
          return
        }

        if (name === "discount" && !validateAgentChargeReduction(name, value)) {
          return
        }

        if (name === "paid_amount" && value !== "") {
          const paidAmount = parseFloat(value)
          const totalAmount = parseFloat(calculateTotalAmount())
          if (paidAmount > totalAmount) {
            Alert.error(`Paid amount (₹${paidAmount.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`)
            return
          }
        }

        setFormData({ ...formData, [name]: value })
      }
    } else {
      const updatedFormData = {
        ...formData,
        [name]: type === "number" ? (value === "" ? "" : Number.parseFloat(value)) : value,
      }

      if (name === "mode_of_payment") {
        if (value === "to_pay" || value === "credit") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
        } else if (value === "paid" || value === "paid_balance") {
          updatedFormData.payment_mode = ""
          updatedFormData.paid_amount = ""
        }
      }

      setFormData(updatedFormData)
    }
  }

  const handleImageChange = (e) => {
    const { name } = e.target
    const file = e.target.files?.[0]
    if (file) {
      setFormData({ ...formData, [name]: file })
      const reader = new FileReader()
      reader.onload = (ev) => {
        if (name === "parcel_image_1") {
          setImagePreview({ ...imagePreview, img1: ev.target.result })
        } else {
          setImagePreview({ ...imagePreview, img2: ev.target.result })
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const focusFieldByName = (name) => {
    const el = document.querySelector(`#edit-shipment-form [name="${name}"]`)
    if (el && !el.disabled) el.focus()
  }

  const getFormFocusables = () => {
    const form = document.getElementById("edit-shipment-form")
    if (!form) return []
    const elements = Array.from(
      form.querySelectorAll(
        "input:not([type=hidden]):not([disabled]):not([type=checkbox]):not([type=radio]):not([type=file]), select:not([disabled]), textarea:not([disabled])",
      ),
    ).filter((el) => el.offsetParent !== null)
    const submitBtn = document.querySelector('button[form="edit-shipment-form"][type="submit"]')
    if (submitBtn) elements.push(submitBtn)
    return elements
  }

  const focusNextField = (currentEl) => {
    const elements = getFormFocusables()
    const index = elements.indexOf(currentEl)
    if (index > -1 && index + 1 < elements.length) {
      elements[index + 1].focus()
    }
  }

  const handleEnterAsTab = (e) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    if (e.target.type === "submit" || e.target.getAttribute("form") === "edit-shipment-form") {
      document.getElementById("edit-shipment-form")?.requestSubmit()
      return
    }
    focusNextField(e.target)
  }

  const handleFieldEnter = (e, nextFieldName) => {
    if (e.key !== "Enter") return
    if (showConsignerSuggestions || showConsigneeSuggestions || showClientDropdown || showArticleDropdown) return
    e.preventDefault()
    if (nextFieldName) {
      focusFieldByName(nextFieldName)
    } else {
      focusNextField(e.target)
    }
  }

  const selectConsignerLocation = (loc) => {
    if (loc.location === formData.consignee_location && loc.location !== "") {
      Alert.error("Sender and Receiver locations cannot be the same")
      return
    }
    setFormData({ ...formData, consigner_location: loc.location })
    setConsignerLocationSearch("")
    setShowConsignerDropdown(false)
    setConsignerHighlightIndex(-1)
  }

  const selectConsigneeLocation = (loc) => {
    if (loc.location === formData.consigner_location && loc.location !== "") {
      Alert.error("Sender and Receiver locations cannot be the same")
      return
    }
    setFormData({ ...formData, consignee_location: loc.location })
    setConsigneeLocationSearch("")
    setShowConsigneeDropdown(false)
    setConsigneeHighlightIndex(-1)
  }

  const selectArticleType = (type) => {
    setFormData((prev) => ({ ...prev, article_type: type }))
    setShowArticleDropdown(false)
    setArticleSearchMode(false)
    setArticleHighlightIndex(-1)
  }

  const handleArticleTypeKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      if (!showArticleDropdown) {
        setShowArticleDropdown(true)
        setArticleSearchMode(false)
      }
      if (filteredArticleTypes.length > 0) {
        setArticleHighlightIndex((prev) => (prev + 1) % filteredArticleTypes.length)
      }
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      if (!showArticleDropdown) {
        setShowArticleDropdown(true)
        setArticleSearchMode(false)
      }
      if (filteredArticleTypes.length > 0) {
        setArticleHighlightIndex((prev) => (prev <= 0 ? filteredArticleTypes.length - 1 : prev - 1))
      }
      return
    }
    if (e.key === "Escape") {
      if (showArticleDropdown) {
        e.preventDefault()
        setShowArticleDropdown(false)
        setArticleHighlightIndex(-1)
      }
      return
    }
    if (e.key === "Enter") {
      if (showArticleDropdown && filteredArticleTypes.length > 0 && articleHighlightIndex >= 0) {
        e.preventDefault()
        selectArticleType(filteredArticleTypes[articleHighlightIndex])
        setTimeout(() => focusNextField(e.target), 0)
        return
      }
    }
    handleEnterAsTab(e)
  }

  const handleLocationBlur = (type) => {
    const isConsigner = type === "consigner"
    const hubs = isConsigner ? consignerLocations : consigneeLocations
    const selected = isConsigner ? formData.consigner_location : formData.consignee_location
    const search = isConsigner ? consignerLocationSearch : consigneeLocationSearch
    const setSearch = isConsigner ? setConsignerLocationSearch : setConsigneeLocationSearch
    const setLocation = (loc) => (isConsigner ? selectConsignerLocation(loc) : selectConsigneeLocation(loc))

    if (selected && isHubLocation(selected, hubs)) return

    const query = search.trim()
    if (!query) {
      if (isConsigner) setFormData((prev) => ({ ...prev, consigner_location: "" }))
      else setFormData((prev) => ({ ...prev, consignee_location: "" }))
      return
    }

    const match = hubs.find((h) => (h.location || "").trim().toLowerCase() === query.toLowerCase())
    if (match) {
      setLocation(match)
    } else {
      setSearch("")
      if (isConsigner) setFormData((prev) => ({ ...prev, consigner_location: "" }))
      else setFormData((prev) => ({ ...prev, consignee_location: "" }))
    }
  }

  const handleLocationDropdownKeyDown = (
    e,
    { isOpen, options, highlightIndex, setHighlightIndex, onSelect, onClose, isLocationSelected },
  ) => {
    if (e.key === "Enter" && !isLocationSelected && (!isOpen || options.length === 0 || highlightIndex < 0)) {
      e.preventDefault()
      Alert.error("Please select a location from the dropdown")
      return
    }
    if (!isOpen || options.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault()
        if (!isLocationSelected) {
          Alert.error("Please select a location from the dropdown")
          return
        }
        focusNextField(e.target)
      }
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightIndex((prev) => (prev + 1) % options.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightIndex >= 0 && highlightIndex < options.length) {
        onSelect(options[highlightIndex])
        setTimeout(() => focusNextField(e.target), 0)
      } else if (isLocationSelected) {
        onClose()
        focusNextField(e.target)
      } else {
        Alert.error("Please select a location from the dropdown")
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      onClose()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.consigner_location || formData.consigner_location.trim() === "") {
      Alert.error("Please select a sender location from the dropdown")
      return
    }
    if (!isHubLocation(formData.consigner_location, consignerLocations)) {
      Alert.error("Please select a valid From Destination from the dropdown list")
      return
    }
    if (!formData.consignee_location || formData.consignee_location.trim() === "") {
      Alert.error("Please select a receiver location from the dropdown")
      return
    }
    if (!isHubLocation(formData.consignee_location, consigneeLocations)) {
      Alert.error("Please select a valid To Destination from the dropdown list")
      return
    }

    const phoneRegex = /^[0-9]\d{9}$/
    if (!phoneRegex.test(formData.consigner_contact)) {
      Alert.error("Sender contact must be a valid 10-digit mobile number")
      return
    }
    if (!phoneRegex.test(formData.consignee_contact)) {
      Alert.error("Receiver contact must be a valid 10-digit mobile number")
      return
    }

    try {
      setSaving(true)

      if (isAgentRole) {
        const restrictedCharges = {
          handling_charges: originalCharges.handling_charges,
          delivery_charges: originalCharges.delivery_charges,
          door_pickup_charges: originalCharges.door_pickup_charges,
          late_charges: originalCharges.late_charges,
          docket_fee: originalCharges.docket_fee,
          other_charges: originalCharges.other_charges,
          discount: originalCharges.discount,
        }

        for (const [field, originalValue] of Object.entries(restrictedCharges)) {
          const currentValue = Number.parseFloat(formData[field]) || 0
          const original = Number.parseFloat(originalValue) || 0
          if (currentValue < original) {
            Alert.error(`Agent role cannot reduce ${field.replace(/_/g, " ")}`)
            setSaving(false)
            return
          }
        }
      }

      if (formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") {
        if (!formData.payment_mode) {
          Alert.error("Please select a payment mode")
          setSaving(false)
          return
        }
        const paidAmount = Number.parseFloat(formData.paid_amount) || 0
        const totalAmount = Number.parseFloat(calculateTotalAmount())
        if (paidAmount > totalAmount) {
          Alert.error(`Paid amount (₹${paidAmount.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`)
          setSaving(false)
          return
        }
      }

      const submitData = {
        ...formData,
        sender_name: formData.consigner_name,
        sender_address: formData.consigner_address,
        sender_location: formData.consigner_location,
        sender_contact: formData.consigner_contact,
        sender_email: formData.consigner_email,
        sender_gst: formData.consigner_gst,
        receiver_name: formData.consignee_name,
        receiver_address: formData.consignee_address,
        receiver_location: formData.consignee_location,
        receiver_contact: formData.consignee_contact,
        receiver_email: formData.consignee_email,
        receiver_gst: formData.consignee_gst,
        eway_bill_numbers: submittedEwayBills.length > 0 ? submittedEwayBills : undefined,
        no_of_articles: Number.parseFloat(formData.no_of_articles) || 1,
        article_amount: Number.parseFloat(formData.article_amount) || 0,
        freight_charges: Number.parseFloat(calculateFreightCharges()) || 0,
        handling_charges: Number.parseFloat(formData.handling_charges) || 0,
        delivery_charges: Number.parseFloat(formData.delivery_charges) || 0,
        door_pickup_charges: Number.parseFloat(formData.door_pickup_charges) || 0,
        late_charges: Number.parseFloat(formData.late_charges) || 0,
        docket_fee: Number.parseFloat(formData.docket_fee) || 0,
        other_charges: Number.parseFloat(formData.other_charges) || 0,
        discount: Number.parseFloat(formData.discount) || 0,
        sgst_percentage: Number.parseFloat(formData.sgst_percentage) || 0,
        cgst_percentage: Number.parseFloat(formData.cgst_percentage) || 0,
        igst_percentage: Number.parseFloat(formData.igst_percentage) || 0,
        paid_amount: Number.parseFloat(formData.paid_amount) || 0,
        payment_mode: formData.payment_mode || null,
        updated_by: user?.user_id,
      }

      if (submittedEwayBills.length > 0) {
        try {
          await shipmentService.addEwayBills(shipmentId, submittedEwayBills)
          Alert.success(`${submittedEwayBills.length} new E-way bill(s) added successfully`)
        } catch (ewayError) {
          console.error("Failed to add e-way bills:", ewayError)
          Alert.error("Shipment updated but failed to add E-way bills")
        }
      }
      await shipmentService.updateShipment(shipmentId, submitData)
      Alert.success("Shipment updated successfully")
      navigate("/shipments")
    } catch (error) {
      console.error("Error updating shipment:", error)
      Alert.error(error.response?.data?.error || "Failed to update shipment")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageLayout title={`Edit Shipment ${shipmentId}`} icon={MapPin} maxWidth="max-w-[100%]">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brand mb-4"></div>
            <p className="text-gray-600 font-semibold">Loading shipment...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={`Edit Shipment ${shipmentId}`}
      icon={MapPin}
      maxWidth="max-w-[100%]"
      className="!p-2 sm:!p-3 lg:!p-4 [&>div>div:first-child]:mb-2"
      actions={
        <>
          <Button variant="secondary" type="button" onClick={() => navigate("/shipments")}>
            Cancel
          </Button>
          <Button variant="secondary" icon={ArrowLeft} type="button" onClick={() => navigate("/shipments")} className="hidden sm:inline-flex">
            Back to Shipments
          </Button>
        </>
      }
    >
      {isAgentRole && (
        <p className="text-xs text-amber-600 font-semibold mb-2 px-1">
          Agent Mode: You can only increase charges, not decrease them
        </p>
      )}

      <form
        id="edit-shipment-form"
        onSubmit={(ev) => {
          ev.preventDefault()
          handleSubmit(ev)
        }}
        className="min-w-0 pb-16 space-y-2"
      >
        {/* Destination & Documents */}
        <div className="bg-white rounded-card shadow-card p-3 min-w-0">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
              <h2 className="text-sm font-semibold text-secondary shrink-0">Destination & Documents</h2>
              <span className="text-xs text-gray-600">
                Shipment ID: <span className="font-semibold text-secondary">{shipmentId}</span>
              </span>
            </div>
            <div className="flex flex-row gap-2 shrink-0">
              <Button variant="secondary" type="button" tabIndex={-1} size="sm" onClick={() => setShowEwayBillModal(true)}>
                Add E-way ID
              </Button>
              <Button
                variant={totalEwayBillCount > 0 ? "primary" : "secondary"}
                type="button"
                tabIndex={-1}
                size="sm"
                onClick={handleViewEwayBills}
              >
                View Eway Bill {totalEwayBillCount > 0 && `(${totalEwayBillCount})`}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex flex-col relative" ref={consignerLocationRef}>
              <label className={LABEL_CLS}>
                From Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="consigner_location_input"
                value={formData.consigner_location || consignerLocationSearch}
                onChange={(e) => {
                  setConsignerLocationSearch(e.target.value)
                  setFormData({ ...formData, consigner_location: "" })
                  setShowConsignerDropdown(true)
                  setConsignerHighlightIndex(-1)
                }}
                onKeyDown={(e) =>
                  handleLocationDropdownKeyDown(e, {
                    isOpen: showConsignerDropdown,
                    options: filteredConsignerLocations,
                    highlightIndex: consignerHighlightIndex,
                    setHighlightIndex: setConsignerHighlightIndex,
                    onSelect: selectConsignerLocation,
                    onClose: () => {
                      setShowConsignerDropdown(false)
                      setConsignerLocationSearch("")
                      setConsignerHighlightIndex(-1)
                    },
                    isLocationSelected:
                      !!formData.consigner_location && isHubLocation(formData.consigner_location, consignerLocations),
                  })
                }
                onFocus={() => {
                  setShowConsignerDropdown(true)
                  setConsignerHighlightIndex(-1)
                }}
                onBlur={() =>
                  setTimeout(() => {
                    setShowConsignerDropdown(false)
                    setConsignerHighlightIndex(-1)
                    handleLocationBlur("consigner")
                  }, 150)
                }
                placeholder="Search location"
                autoComplete="off"
                className={`${INPUT_CLS} ${formData.consigner_location ? "" : INPUT_ERROR_CLS}`}
              />
              {showConsignerDropdown && (
                <div className="absolute z-30 w-full mt-1 top-full bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                  {filteredConsignerLocations.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">No locations found</div>
                  ) : (
                    filteredConsignerLocations.map((loc, idx) => (
                      <div
                        key={loc.hub_id || loc.id || idx}
                        onMouseEnter={() => setConsignerHighlightIndex(idx)}
                        onMouseDown={() => selectConsignerLocation(loc)}
                        className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 ${idx === consignerHighlightIndex ? "bg-brand-light text-brand font-medium" : "hover:bg-blue-50"}`}
                      >
                        {loc.location}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col relative" ref={consigneeLocationRef}>
              <label className={LABEL_CLS}>
                To Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="consignee_location_input"
                value={formData.consignee_location || consigneeLocationSearch}
                onChange={(e) => {
                  setConsigneeLocationSearch(e.target.value)
                  setFormData({ ...formData, consignee_location: "" })
                  setShowConsigneeDropdown(true)
                  setConsigneeHighlightIndex(-1)
                }}
                onKeyDown={(e) =>
                  handleLocationDropdownKeyDown(e, {
                    isOpen: showConsigneeDropdown,
                    options: filteredConsigneeLocations,
                    highlightIndex: consigneeHighlightIndex,
                    setHighlightIndex: setConsigneeHighlightIndex,
                    onSelect: selectConsigneeLocation,
                    onClose: () => {
                      setShowConsigneeDropdown(false)
                      setConsigneeLocationSearch("")
                      setConsigneeHighlightIndex(-1)
                    },
                    isLocationSelected:
                      !!formData.consignee_location && isHubLocation(formData.consignee_location, consigneeLocations),
                  })
                }
                onFocus={() => {
                  setShowConsigneeDropdown(true)
                  setConsigneeHighlightIndex(-1)
                }}
                onBlur={() =>
                  setTimeout(() => {
                    setShowConsigneeDropdown(false)
                    setConsigneeHighlightIndex(-1)
                    handleLocationBlur("consignee")
                  }, 150)
                }
                placeholder="Search location"
                autoComplete="off"
                className={`${INPUT_CLS} ${formData.consignee_location ? "" : INPUT_ERROR_CLS}`}
              />
              {showConsigneeDropdown && (
                <div className="absolute z-30 w-full mt-1 top-full bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                  {filteredConsigneeLocations.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">No locations found</div>
                  ) : (
                    filteredConsigneeLocations.map((loc, idx) => (
                      <div
                        key={loc.hub_id || loc.id || idx}
                        onMouseEnter={() => setConsigneeHighlightIndex(idx)}
                        onMouseDown={() => selectConsigneeLocation(loc)}
                        className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 ${idx === consigneeHighlightIndex ? "bg-brand-light text-brand font-medium" : "hover:bg-blue-50"}`}
                      >
                        {loc.location}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className={LABEL_CLS}>Invoice Number</label>
              <input
                type="text"
                name="invoice_number"
                placeholder="Optional"
                value={formData.invoice_number}
                onChange={handleChange}
                onKeyDown={(e) =>
                  handleFieldEnter(
                    e,
                    useShipmentClient ? (selectedClient ? "consignee_name" : "client_search") : "consigner_name",
                  )
                }
                className={INPUT_CLS}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 min-w-0">
          {/* Left column */}
          <div className="space-y-2 min-w-0">
            <div className="bg-white rounded-card shadow-card p-3">
              <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-secondary">Consignor Information</h2>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      id="useShipmentClient"
                      checked={useShipmentClient}
                      onChange={(e) => handleUseClientChange(e.target.checked)}
                      tabIndex={-1}
                      className="w-3.5 h-3.5 text-brand border-gray-300 rounded"
                    />
                    Existing client
                  </label>
                  {hasConsignorExtraData && (
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowConsignorExtra(!showConsignorExtra)}
                      className="p-1 rounded-input hover:bg-gray-100 transition"
                      aria-label="More consignor details"
                    >
                      {showConsignorExtra ? (
                        <ChevronUp className="w-4 h-4 text-secondary" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-secondary" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col relative">
                  <label className={LABEL_CLS}>
                    Consignor Name <span className="text-red-500">*</span>
                  </label>
                  {useShipmentClient ? (
                    <>
                      <input
                        type="text"
                        name="client_search"
                        value={selectedClient ? `${selectedClient.full_name} (${selectedClient.company_name})` : clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value)
                          setSelectedClient(null)
                          setShowClientDropdown(true)
                        }}
                        onKeyDown={(e) => handleFieldEnter(e, "consigner_contact")}
                        onFocus={() => setShowClientDropdown(true)}
                        placeholder="Search client"
                        autoComplete="off"
                        className={INPUT_CLS}
                        required
                      />
                      {showClientDropdown && filteredClients.length > 0 && !selectedClient && (
                        <div className="absolute z-20 w-full mt-1 top-full bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                          {filteredClients.map((client) => (
                            <div
                              key={client.id}
                              onMouseDown={() => handleClientSelect(client)}
                              className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium">{client.full_name}</div>
                              <div className="text-xs text-gray-600">{client.company_name}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        name="consigner_name"
                        placeholder="Full Name"
                        value={formData.consigner_name}
                        onChange={(e) => {
                          handleChange(e)
                          fetchCustomerSuggestions("consigner", e.target.value, "")
                          setShowConsignerSuggestions(true)
                        }}
                        onKeyDown={(e) => handleFieldEnter(e, "consigner_contact")}
                        onBlur={() => setTimeout(() => setShowConsignerSuggestions(false), 150)}
                        autoComplete="off"
                        required
                        className={INPUT_CLS}
                      />
                      {showConsignerSuggestions && consignerSuggestions.length > 0 && (
                        <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                          {consignerSuggestions.map((s, i) => (
                            <div
                              key={i}
                              onMouseDown={() => handleSelectSuggestion("consigner", s)}
                              className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                            >
                              <span className="font-medium">{s.name}</span>
                              <span className="text-gray-500 ml-2">— {s.contact}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className={LABEL_CLS}>
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="consigner_contact"
                    placeholder="10 digit mobile"
                    value={formData.consigner_contact}
                    onChange={(e) => {
                      handleChange(e)
                      if (!useShipmentClient) {
                        fetchCustomerSuggestions("consigner", "", e.target.value)
                        setShowConsignerSuggestions(true)
                      }
                    }}
                    onKeyDown={(e) => handleFieldEnter(e, "consignee_name")}
                    onBlur={() => setTimeout(() => setShowConsignerSuggestions(false), 150)}
                    disabled={useShipmentClient && selectedClient}
                    autoComplete="off"
                    required
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              {showConsignorExtra && hasConsignorExtraData && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 pt-2 border-t border-gray-100">
                  {formData.consigner_address?.trim() && (
                    <div className="flex flex-col sm:col-span-2">
                      <label className={LABEL_CLS}>Address</label>
                      <input type="text" name="consigner_address" value={formData.consigner_address} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                    </div>
                  )}
                  {formData.consigner_email?.trim() && (
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Email</label>
                      <input type="email" name="consigner_email" value={formData.consigner_email} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                    </div>
                  )}
                  {formData.consigner_gst?.trim() && (
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>GST Number</label>
                      <input type="text" name="consigner_gst" value={formData.consigner_gst} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-card shadow-card p-3">
              <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-secondary">Consignee Information</h2>
                {hasConsigneeExtraData && (
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowConsigneeExtra(!showConsigneeExtra)}
                    className="p-1 rounded-input hover:bg-gray-100 transition"
                    aria-label="More consignee details"
                  >
                    {showConsigneeExtra ? (
                      <ChevronUp className="w-4 h-4 text-secondary" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-secondary" />
                    )}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className={LABEL_CLS}>
                    Consignee Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="consignee_name"
                      value={formData.consignee_name}
                      onChange={(e) => {
                        handleChange(e)
                        fetchCustomerSuggestions("consignee", e.target.value, "")
                        setShowConsigneeSuggestions(true)
                      }}
                      onKeyDown={(e) => handleFieldEnter(e, "consignee_contact")}
                      onBlur={() => setTimeout(() => setShowConsigneeSuggestions(false), 150)}
                      autoComplete="off"
                      required
                      className={INPUT_CLS}
                    />
                    {showConsigneeSuggestions && consigneeSuggestions.length > 0 && (
                      <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-input shadow-lg max-h-40 overflow-y-auto">
                        {consigneeSuggestions.map((s, i) => (
                          <div
                            key={i}
                            onMouseDown={() => handleSelectSuggestion("consignee", s)}
                            className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                          >
                            <span className="font-medium">{s.name}</span>
                            <span className="text-gray-500 ml-2">— {s.contact}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className={LABEL_CLS}>
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="consignee_contact"
                    value={formData.consignee_contact}
                    onChange={(e) => {
                      handleChange(e)
                      fetchCustomerSuggestions("consignee", "", e.target.value)
                      setShowConsigneeSuggestions(true)
                    }}
                    onKeyDown={(e) => handleFieldEnter(e, "article_type")}
                    onBlur={() => setTimeout(() => setShowConsigneeSuggestions(false), 150)}
                    autoComplete="off"
                    required
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              {showConsigneeExtra && hasConsigneeExtraData && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 pt-2 border-t border-gray-100">
                  {formData.consignee_address?.trim() && (
                    <div className="flex flex-col sm:col-span-2">
                      <label className={LABEL_CLS}>Address</label>
                      <input type="text" name="consignee_address" value={formData.consignee_address} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                    </div>
                  )}
                  {formData.consignee_email?.trim() && (
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Email</label>
                      <input type="email" name="consignee_email" value={formData.consignee_email} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                    </div>
                  )}
                  {formData.consignee_gst?.trim() && (
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>GST Number</label>
                      <input type="text" name="consignee_gst" value={formData.consignee_gst} readOnly className={`${INPUT_CLS} bg-gray-50`} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-2 min-w-0">
            <div className="bg-white rounded-card shadow-card p-3">
              <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-secondary">Shipment Details</h2>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowShipmentExtra(!showShipmentExtra)}
                  className="p-1 rounded-input hover:bg-gray-100 transition"
                  aria-label="More shipment details"
                >
                  {showShipmentExtra ? (
                    <ChevronUp className="w-4 h-4 text-secondary" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-secondary" />
                  )}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col relative">
                  <label className={LABEL_CLS}>
                    Article Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type="text"
                      name="article_type"
                      placeholder="TYPE OR SELECT"
                      value={formData.article_type || ""}
                      onChange={(e) => {
                        setFormData((prev) => ({ ...prev, article_type: e.target.value.toUpperCase() }))
                        setArticleSearchMode(true)
                        setShowArticleDropdown(true)
                        setArticleHighlightIndex(-1)
                      }}
                      onFocus={() => {
                        setArticleSearchMode(false)
                        setShowArticleDropdown(true)
                        setArticleHighlightIndex(-1)
                      }}
                      onBlur={() =>
                        setTimeout(() => {
                          setShowArticleDropdown(false)
                          setArticleSearchMode(false)
                          setArticleHighlightIndex(-1)
                        }, 150)
                      }
                      onKeyDown={handleArticleTypeKeyDown}
                      required
                      className={`${INPUT_CLS} pr-8 uppercase`}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setArticleSearchMode(false)
                        setArticleHighlightIndex(-1)
                        setShowArticleDropdown((prev) => !prev)
                      }}
                      className="absolute right-2 text-gray-500 hover:text-gray-700"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  {showArticleDropdown && (
                    <div className="absolute z-20 w-full top-full mt-1 bg-white border border-gray-300 rounded-input shadow-lg overflow-y-auto max-h-32">
                      {filteredArticleTypes.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400 text-center">No article types found</div>
                      ) : (
                        filteredArticleTypes.map((type, idx) => (
                          <div
                            key={type}
                            onMouseEnter={() => setArticleHighlightIndex(idx)}
                            onMouseDown={() => selectArticleType(type)}
                            className={`px-3 py-1.5 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 uppercase ${idx === articleHighlightIndex ? "bg-brand-light text-brand font-medium" : "hover:bg-blue-50"}`}
                          >
                            {type}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className={LABEL_CLS}>
                    Number of Articles <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="no_of_articles"
                    value={formData.no_of_articles}
                    onChange={handleChange}
                    onKeyDown={handleEnterAsTab}
                    min="1"
                    required
                    className={INPUT_CLS}
                  />
                </div>
                <div className="flex flex-col">
                  <label className={LABEL_CLS}>
                    Article Rate <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="article_amount"
                    value={formData.article_amount}
                    onChange={handleChange}
                    onKeyDown={(e) => handleFieldEnter(e, "handling_charges")}
                    required
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              {showShipmentExtra && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 pt-2 border-t border-gray-100">
                  <div className="flex flex-col sm:col-span-3">
                    <label className={LABEL_CLS}>Parcel Details</label>
                    <textarea
                      name="parcel_detail"
                      value={formData.parcel_detail}
                      onChange={handleChange}
                      onKeyDown={handleEnterAsTab}
                      rows="1"
                      className={`${INPUT_CLS} resize-none`}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Weight (kg)</label>
                    <input type="text" name="weight" value={formData.weight} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Shipment Value</label>
                    <input type="text" name="shipment_value" value={formData.shipment_value} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Parcel Image 1</label>
                    <div className="border border-dashed border-gray-300 rounded-input p-1.5 text-center hover:border-brand cursor-pointer">
                      <input type="file" name="parcel_image_1" onChange={handleImageChange} accept="image/*" capture="environment" className="hidden" id="image1Input" />
                      <label htmlFor="image1Input" className="cursor-pointer block">
                        {imagePreview.img1 || existingImages.img1 ? (
                          <img src={imagePreview.img1 || existingImages.img1} alt="Preview 1" className="w-full h-12 object-contain" />
                        ) : (
                          <p className="text-xs text-gray-500 py-3">Upload</p>
                        )}
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Parcel Image 2</label>
                    <div className="border border-dashed border-gray-300 rounded-input p-1.5 text-center hover:border-brand cursor-pointer">
                      <input type="file" name="parcel_image_2" onChange={handleImageChange} accept="image/*" capture="environment" className="hidden" id="image2Input" />
                      <label htmlFor="image2Input" className="cursor-pointer block">
                        {imagePreview.img2 || existingImages.img2 ? (
                          <img src={imagePreview.img2 || existingImages.img2} alt="Preview 2" className="w-full h-12 object-contain" />
                        ) : (
                          <p className="text-xs text-gray-500 py-3">Upload</p>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-2 mt-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-secondary">Shipment Charges</h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowGSTDetails(!showGSTDetails)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-input transition"
                    >
                      GST {showGSTDetails ? "▴" : "▾"}
                    </button>
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand bg-brand-light hover:bg-blue-100 border border-blue-200 rounded-input transition"
                    >
                      Additional {showAdditionalCharges ? "▴" : "▾"}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Freight Charges</label>
                    <input type="text" value={calculateFreightCharges()} disabled className={`${INPUT_CLS} bg-gray-100 text-gray-600 font-semibold cursor-not-allowed`} />
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Handling Charges</label>
                    <input type="text" name="handling_charges" value={formData.handling_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Discount</label>
                    <input type="text" name="discount" value={formData.discount} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                  </div>
                </div>
                {showGSTDetails && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 border border-gray-200 rounded-input mb-3">
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>SGST %</label>
                      <input type="text" name="sgst_percentage" value={formData.sgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>CGST %</label>
                      <input type="text" name="cgst_percentage" value={formData.cgst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>IGST %</label>
                      <input type="text" name="igst_percentage" value={formData.igst_percentage} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="bg-purple-50 p-2 rounded-input">
                      <p className="text-[10px] text-gray-600 font-semibold">SGST</p>
                      <p className="text-sm font-bold text-purple-600">₹{calculateGSTAmounts().sgst}</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded-input">
                      <p className="text-[10px] text-gray-600 font-semibold">CGST</p>
                      <p className="text-sm font-bold text-purple-600">₹{calculateGSTAmounts().cgst}</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded-input">
                      <p className="text-[10px] text-gray-600 font-semibold">IGST</p>
                      <p className="text-sm font-bold text-purple-600">₹{calculateGSTAmounts().igst}</p>
                    </div>
                  </div>
                )}
                {showAdditionalCharges && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 p-3 border border-gray-200 rounded-input mb-3">
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Door Pickup</label>
                      <input type="text" name="door_pickup_charges" value={formData.door_pickup_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Door Delivery</label>
                      <input type="text" name="delivery_charges" value={formData.delivery_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Docket Fee</label>
                      <input type="text" name="docket_fee" value={formData.docket_fee} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Late Charges</label>
                      <input type="text" name="late_charges" value={formData.late_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Other Charges</label>
                      <input type="text" name="other_charges" value={formData.other_charges} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Total Amount</label>
                    <div className={`${INPUT_CLS} bg-gray-100 font-bold text-green-600`}>₹{calculateTotalAmount()}</div>
                  </div>
                  <div className="flex flex-col">
                    <label className={LABEL_CLS}>Payment Status</label>
                    <select name="mode_of_payment" value={formData.mode_of_payment} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS}>
                      <option value="to_pay">To Pay</option>
                      <option value="credit">Credit</option>
                      <option value="paid">Paid</option>
                      <option value="foc">FOC (Free of Charge)</option>
                    </select>
                  </div>
                  {formData.mode_of_payment !== "to_pay" && formData.mode_of_payment !== "credit" && formData.mode_of_payment !== "foc" && (
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Payment Mode</label>
                      <select name="payment_mode" value={formData.payment_mode} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS}>
                        <option value="">Select Mode</option>
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cheque">Cheque</option>
                        <option value="online">Online</option>
                      </select>
                    </div>
                  )}
                  {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                    <div className="flex flex-col">
                      <label className={LABEL_CLS}>Paid Amount</label>
                      <input type="text" name="paid_amount" value={formData.paid_amount} onChange={handleChange} onKeyDown={handleEnterAsTab} className={INPUT_CLS} />
                    </div>
                  )}
                </div>
                {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-brand-light p-2 rounded-input">
                      <p className="text-[10px] text-gray-600 font-semibold">Paid</p>
                      <p className="text-sm font-bold text-brand">₹{formData.paid_amount || "0.00"}</p>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-input">
                      <p className="text-[10px] text-gray-600 font-semibold">Balance Due</p>
                      <p className="text-sm font-bold text-brand">₹{calculateBalanceAmount()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="fixed bottom-4 right-4 z-40 lg:right-8">
        <Button variant="primary" type="submit" form="edit-shipment-form" loading={saving} disabled={saving} onKeyDown={handleEnterAsTab}>
          {saving ? "Updating..." : "Update Shipment"}
        </Button>
      </div>

      {showEwayBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-sm shadow-lg w-[95%] max-w-md">
            <div className="flex items-center justify-between px-4 py-2 bg-brand text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Add E-WayBill</h3>
              <button onClick={handleCloseEwayBillModal}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              {ewayBills.map((value, index) => {
                const isValid = isValidEway(value)
                return (
                  <div key={index}>
                    <input
                      type="text"
                      value={value}
                      maxLength={12}
                      inputMode="numeric"
                      placeholder="Enter 12-digit E-Way Bill No."
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "")
                        const updated = [...ewayBills]
                        updated[index] = val
                        setEwayBills(updated)
                      }}
                      className={`w-full px-3 py-2 border rounded-sm ${value && !isValid ? "border-red-500" : "border-gray-300"} focus:outline-none`}
                    />
                    {value && !isValid && <p className="text-xs text-red-500 mt-1">E-Way Bill must be exactly 12 digits</p>}
                  </div>
                )
              })}
              <div className="flex gap-2 pt-2">
                <button
                  disabled={!isValidEway(ewayBills[ewayBills.length - 1])}
                  onClick={() => setEwayBills([...ewayBills, ""])}
                  className={`px-3 py-2 text-sm rounded-sm text-white ${isValidEway(ewayBills[ewayBills.length - 1]) ? "bg-sky-500 hover:bg-sky-600" : "bg-gray-300 cursor-not-allowed"}`}
                >
                  Add New
                </button>
                <button
                  disabled={ewayBills.length === 1}
                  onClick={() => setEwayBills(ewayBills.slice(0, -1))}
                  className={`px-3 py-2 text-sm rounded-sm text-white ${ewayBills.length > 1 ? "bg-red-500 hover:bg-red-600" : "bg-gray-300 cursor-not-allowed"}`}
                >
                  Remove Last
                </button>
                <button
                  onClick={handleSubmitEwayBills}
                  disabled={!ewayBills.some(isValidEway)}
                  className={`px-3 py-2 text-sm rounded-sm text-white ${ewayBills.some(isValidEway) ? "bg-green-500 hover:bg-green-600" : "bg-gray-300 cursor-not-allowed"}`}
                >
                  Submit
                </button>
              </div>
            </div>
            <div className="flex justify-end px-4 py-2 border-t">
              <button onClick={handleCloseEwayBillModal} className="px-3 py-1.5 text-sm border rounded-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showViewEwayBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-sm shadow-lg w-[95%] max-w-md">
            <div className="flex items-center justify-between px-4 py-2 bg-green-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">E-Way Bills ({totalEwayBillCount})</h3>
              <button onClick={() => setShowViewEwayBillModal(false)}>✕</button>
            </div>
            <div className="p-4">
              {loadingEwayBills ? (
                <p className="text-sm text-gray-600 text-center py-4">Loading E-way bills...</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {existingEwayBills.map((bill, index) => (
                    <div
                      key={bill.id}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-6 h-6 bg-green-600 text-white text-xs font-bold rounded-full">
                          {index + 1}
                        </span>
                        <div>
                          <span className="text-sm font-semibold text-gray-800">{bill.eway_bill_number}</span>
                          <span className="text-[10px] text-gray-500 ml-2">(saved)</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteEwayBill(bill.id, bill.eway_bill_number)}
                        className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-sm transition"
                        title="Delete this E-way bill"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                  {submittedEwayBills.map((bill, index) => (
                    <div
                      key={`new-${index}`}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-6 h-6 bg-brand text-white text-xs font-bold rounded-full">
                          {existingEwayBills.length + index + 1}
                        </span>
                        <div>
                          <span className="text-sm font-semibold text-gray-800">{bill}</span>
                          <span className="text-[10px] text-gray-500 ml-2">(pending)</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveSubmittedEwayBill(index)}
                        className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-sm transition"
                        title="Remove from queue"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-between px-4 py-2 border-t">
              <button
                onClick={() => {
                  setShowViewEwayBillModal(false)
                  setShowEwayBillModal(true)
                }}
                className="px-3 py-1.5 text-sm bg-brand hover:bg-brand text-white rounded-sm"
              >
                Add More
              </button>
              <button onClick={() => setShowViewEwayBillModal(false)} className="px-3 py-1.5 text-sm border rounded-sm hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default EditShipment
